/**
 * Word-level diff between a learner's original journal text and the corrected
 * version, for the inline "What changed" view.
 *
 * diffWords(before, after) -> [{ type: 'equal', text } | { type: 'change', removed, added }]
 *   - Words are compared exactly (accents and punctuation count as changes).
 *   - Whitespace/newlines are kept from the corrected text so paragraphs render.
 *   - Returns null for very long texts (the LCS table would be too big); callers
 *     should fall back to showing the two versions separately.
 *
 * findReasons(segments, removed, added) -> up to 3 analysis segments that explain
 *   a change (matched on each segment's text/correction), best match first. A
 *   change can merge several neighbouring corrections, so there may be more than one.
 */
const MAX_CELLS = 2_000_000;

function tokenize(text) {
  // [{ word, ws }] — each word with the whitespace that follows it.
  const out = [];
  const re = /(\S+)(\s*)/g;
  let m;
  const lead = (text.match(/^\s*/) || [''])[0];
  while ((m = re.exec(text)) !== null) out.push({ word: m[1], ws: m[2] });
  return { lead, tokens: out };
}

export function diffWords(before = '', after = '') {
  const a = tokenize(before || '').tokens;
  const { lead, tokens: b } = tokenize(after || '');
  const n = a.length;
  const m = b.length;
  if ((n + 1) * (m + 1) > MAX_CELLS) return null;

  // LCS lengths, suffix form: L[i][j] = LCS of a[i..], b[j..]
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      L[i][j] = a[i].word === b[j].word ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    }
  }

  const ops = [];
  const push = (type, text) => {
    const last = ops[ops.length - 1];
    if (last && last.type === type) last.text += text;
    else ops.push({ type, text });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i].word === b[j].word) { push('equal', b[j].word + b[j].ws); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) { push('del', a[i].word + ' '); i++; }
    else { push('ins', b[j].word + b[j].ws); j++; }
  }
  while (i < n) { push('del', a[i].word + ' '); i++; }
  while (j < m) { push('ins', b[j].word + b[j].ws); j++; }

  // Fold adjacent del/ins runs into one change so each correction reads as a unit.
  const result = lead ? [{ type: 'equal', text: lead }] : [];
  for (let k = 0; k < ops.length; k++) {
    const op = ops[k];
    if (op.type === 'equal') { result.push(op); continue; }
    const change = { type: 'change', removed: '', added: '' };
    while (k < ops.length && ops[k].type !== 'equal') {
      if (ops[k].type === 'del') change.removed += ops[k].text;
      else change.added += ops[k].text;
      k++;
    }
    k--;
    result.push(change);
  }
  return result;
}

const norm = (t) => (t || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();

export function findReasons(segments, removed, added) {
  const r = norm(removed);
  const a = norm(added);
  const scored = [];
  for (const seg of segments || []) {
    if (!seg || !seg.correction) continue;
    const st = norm(seg.text);
    const sc = norm(seg.correction);
    let score = 0;
    if (a && sc && (sc.includes(a) || a.includes(sc))) score += 2;
    if (r && st && (st.includes(r) || r.includes(st))) score += 2;
    if (score > 0) scored.push({ seg, score });
  }
  scored.sort((x, y) => y.score - x.score);
  return scored.slice(0, 3).map((s) => s.seg);
}
